import { ChangeEventHandler, MouseEventHandler, useState, KeyboardEventHandler, forwardRef, LegacyRef } from 'react';
import './TextInput.css';

type TextInputProps = {
	defaultValue?: string;
	className?: string;
	placeholder: string;
	id?: string;
	name?: string;
	autocomplete?:
		| 'off'
		| 'name'
		| 'honorific-prefix'
		| 'given-name'
		| 'additional-name'
		| 'family-name'
		| 'honorific-suffix'
		| 'nickname'
		| 'username'
		| 'new-password'
		| 'current-password'
		| 'one-time-code'
		| 'organization-title'
		| 'organization'
		| 'street-address'
		| 'address-line1'
		| 'address-line2'
		| 'address-line3'
		| 'address-level4'
		| 'address-level3'
		| 'address-level2'
		| 'address-level1'
		| 'country'
		| 'country-name'
		| 'postal-code'
		| 'cc-name'
		| 'cc-given-name'
		| 'cc-additional-name'
		| 'cc-family-name'
		| 'cc-number'
		| 'cc-exp'
		| 'cc-exp-month'
		| 'cc-exp-year'
		| 'cc-csc'
		| 'cc-type'
		| 'transaction-currency'
		| 'transaction-amount'
		| 'language'
		| 'bday'
		| 'bday-day'
		| 'bday-month'
		| 'bday-year'
		| 'sex'
		| 'url'
		| 'photo'
		| 'home'
		| 'work'
		| 'mobile'
		| 'fax'
		| 'pager'
		| 'tel'
		| 'tel-country-code'
		| 'tel-national'
		| 'tel-area-code'
		| 'tel-local'
		| 'tel-local-prefix'
		| 'tel-local-suffix'
		| 'tel-extension'
		| 'email'
		| 'impp'
		| 'delivery-zip'
		| 'invoice-zip'
		| 'billing postal-code';
	value?: string;
	onChange?: ChangeEventHandler<HTMLInputElement>;
	onBlur?: (e: any) => void;
	onFocus?: (inputValue?: string) => void;
	type?: 'text' | 'password' | 'email';
	isValid?: boolean;
	status?: 'error' | 'success' | 'warning';
	autoFocus?: boolean;
	toggleableLabel?: {
		text: string;
		onClick?: MouseEventHandler<HTMLLabelElement>;
		id?: string;
	};
	onKeyDown?: KeyboardEventHandler<HTMLInputElement>;
	nextElement?: string;
	readOnly?: boolean;
	maxLength?: number;
};

const safeName = (s: string) => s.replace(/[^a-zA-Z]/g, '_');

export const TextInputComponent = (
	{
		className,
		defaultValue,
		name,
		placeholder,
		id,
		autocomplete,
		value,
		onChange,
		onBlur,
		onFocus,
		type,
		isValid,
		status,
		autoFocus,
		toggleableLabel,
		onKeyDown,
		nextElement,
		readOnly,
		maxLength,
	}: TextInputProps,
	ref: LegacyRef<HTMLInputElement> | undefined
): JSX.Element => {
	const [focused, setFocused] = useState(false);
	const [touched, setTouched] = useState(false);

	const invalid = touched && (isValid === false || status === 'error' || status === 'warning');

	return (
		<text-input class={className}>
			{!!toggleableLabel && (
				<div className="toggle-label-wrapper">
					<label className="toggle-label" id={toggleableLabel.id ?? ''} onClick={toggleableLabel.onClick}>
						{toggleableLabel.text}
					</label>
				</div>
			)}
			<input
				readOnly={readOnly}
				defaultValue={defaultValue}
				ref={ref}
				type={type ?? 'text'}
				id={id ?? autocomplete ?? safeName(placeholder)}
				name={id ?? name ?? undefined}
				autoComplete={autocomplete}
				className={invalid ? ` ${status ?? 'error'}` : ' valid'}
				onFocus={(e) => {
					if (!touched) {
						setTouched(true);
					}
					setFocused(true);
					if (onFocus) return onFocus(e.target.value);
				}}
				onBlur={(e) => {
					setFocused(false);
					if (onBlur) onBlur(e);
				}}
				value={value}
				onChange={(e) => {
					if (onChange) return onChange(e);
				}}
				autoFocus={autoFocus}
				onKeyDown={(e) => {
					if (onKeyDown) return onKeyDown(e);
				}}
				data-nextelement={nextElement}
				maxLength={maxLength}
			/>
			<span className={`placeholder${focused || value ? ' small' : ''}`}>{placeholder}</span>
		</text-input>
	);
};

export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(TextInputComponent);
